import { Icon } from '@progress/kendo-react-common';
import { RadioButton, RadioButtonChangeEvent } from '@progress/kendo-react-inputs';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import SearchBox from 'src/components/design-system/SearchBox';

export interface FolderOption {
  id: number;
  name: string;
}

interface AddToFavoritesDropdownProps {
  folders?: FolderOption[];
  onAdd?: (folderId: number) => Promise<void>;
  disabled?: boolean;
  isLoading?: boolean;
}

const AddToFavoritesDropdown: FC<AddToFavoritesDropdownProps> = ({
  folders = [],
  onAdd,
  disabled = false,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleButtonClick = useCallback(() => {
    if (disabled) return;
    setIsDropdownOpen((prev) => !prev);
  }, [disabled]);

  const handleFolderSelect = useCallback((event: RadioButtonChangeEvent) => {
    const folderId = typeof event.value === 'string' ? parseInt(event.value, 10) : event.value;
    setSelectedFolder(folderId);
  }, []);

  const handleAddToFavorites = useCallback(async () => {
    if (selectedFolder !== null && onAdd) {
      setIsDropdownOpen(false);
      setSelectedFolder(null);
      setSearchValue('');
      await onAdd(selectedFolder);
    }
  }, [selectedFolder, onAdd]);

  const handleCloseDropdown = useCallback(() => {
    setIsDropdownOpen(false);
    setSelectedFolder(null);
    setSearchValue('');
  }, []);

  const filteredFolders = useMemo(() => {
    if (!searchValue.trim()) {
      return folders;
    }
    return folders.filter((folder: FolderOption) =>
      folder.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [searchValue, folders]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSelectedFolder(null);
        setSearchValue('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div ref={dropdownRef} className="relative">
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
        className="max-h-[180px]"
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper
              type="Information"
              contents={[t('Please select at least one item to Add to Favorites')]}
            />
          </div>
        }
        show={disabled}
      >
        <Button
          color="secondary"
          size="md"
          onClick={handleButtonClick}
          endIcon={<Icon name={isDropdownOpen ? 'caret-alt-up' : 'caret-alt-down'} />}
          disabled={disabled}
        >
          {t('Add to Favorites')}
        </Button>
      </PopUp>

      {isDropdownOpen && (
        <div
          className="k-animation-container k-animation-container-shown absolute z-50 mt-1"
          style={{
            width: '290px',
            left: 0,
          }}
        >
          <div className="k-popup k-group k-reset k-list-container bg-white border border-gray-300 shadow-lg rounded-md">
            {/* Header */}
            <div
              className="flex items-center justify-between px-3 py-2 bg-gray-200 text-white rounded-t-md"
              style={{ height: '50px' }}
            >
              <h3 className="text-sm font-semibold text-black">{t('Select a Folder')}</h3>
              <button
                type="button"
                onClick={handleCloseDropdown}
                className="text-black hover:bg-gray-100 px-1 cursor-pointer"
                aria-label="Close"
              >
                <Icon name="close" />
              </button>
            </div>

            {/* Search Box */}
            <div className="px-2 py-2" style={{ height: '56px' }}>
              <SearchBox
                placeholder={t('Search')}
                width="100%"
                value={searchValue}
                onChange={setSearchValue}
              />
            </div>

            {/* Folder List with Radio Buttons */}
            <div
              className="overflow-auto"
              style={{
                maxHeight: '220px',
              }}
            >
              {filteredFolders.length > 0 ? (
                <div className="px-2 py-1">
                  {filteredFolders.map((folder: FolderOption) => (
                    <div key={folder.id} className="flex items-center mb-1">
                      <RadioButton
                        value={folder.id.toString()}
                        checked={selectedFolder === folder.id}
                        onChange={handleFolderSelect}
                      />
                      <label
                        className="text-sm cursor-pointer ml-2"
                        onClick={() => setSelectedFolder(folder.id)}
                      >
                        {folder.name}
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500 text-sm">
                  <Icon name="warning" className="text-2xl mb-2" />
                  <p>{t('No Favorite Folders Found')}</p>
                </div>
              )}
            </div>

            {/* Add Button */}
            <div className="px-2 py-2 flex justify-start rounded-b-md">
              <PopUp
                popupAlign={{ horizontal: 'left', vertical: 'top' }}
                anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
                className="max-h-[180px]"
                content={
                  <div className="m-1 max-w-[350px]">
                    <PopUpContentWrapper
                      type="Information"
                      contents={[t('Please select a folder')]}
                    />
                  </div>
                }
                show={selectedFolder === null}
              >
                <Button
                  color="info"
                  size="lg"
                  onClick={handleAddToFavorites}
                  disabled={selectedFolder === null || isLoading}
                  loading={isLoading}
                >
                  {t('Add')}
                </Button>
              </PopUp>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddToFavoritesDropdown;
